import React, { useState } from 'react';
import { Button, Card, Empty } from 'antd';
import { EditOutlined } from '@ant-design/icons';

import { formatDateTime, formatDistance } from './dateUtils';
import LocationAnalysisMap from './LocationAnalysisMap';
import LocationAnalysisTimeline from './LocationAnalysisTimeline';
import type {
  DerivedLocation,
  DerivedStaypoint,
  DerivedTimelineItem,
  DerivedTimelineTarget,
  DerivedTrip,
  DerivedTripleg,
  LocationAnalysisTimelineActions,
  POICandidate,
  POIEnrichment,
} from './timelineTypes';
import './LocationAnalysisResultView.css';

type LocationAnalysisResultViewProps = LocationAnalysisTimelineActions & {
  items: DerivedTimelineItem[];
  staypoints: DerivedStaypoint[];
  triplegs: DerivedTripleg[];
  trips: DerivedTrip[];
  locations: DerivedLocation[];
  visibleLocations?: DerivedLocation[];
  locationExtentBufferM: number;
  locationNameMap: Map<string, string>;
  locationPoiMap: Map<string, POIEnrichment>;
  poiCandidatesByLocationId: Map<string, POICandidate[]>;
  searchingPoiLocationId: string | null;
  linkingPoiLocationId: string | null;
  selectedTarget: DerivedTimelineTarget | null;
  framed?: boolean;
  className?: string;
  mapTitle?: string;
  timelineTitle?: string;
  mapHeight?: number | string;
  showTimelineEditToggle?: boolean;
};

type LocationAnalysisTimelinePanelProps = LocationAnalysisTimelineActions & {
  items: DerivedTimelineItem[];
  locations: DerivedLocation[];
  locationNameMap: Map<string, string>;
  locationPoiMap: Map<string, POIEnrichment>;
  poiCandidatesByLocationId: Map<string, POICandidate[]>;
  searchingPoiLocationId: string | null;
  linkingPoiLocationId: string | null;
  selectedTarget: DerivedTimelineTarget | null;
  timelineTitle?: string;
  showTimelineEditToggle?: boolean;
  editButtonLabel?: string;
  editingButtonLabel?: string;
  editButtonStyle?: React.CSSProperties;
  className?: string;
  style?: React.CSSProperties;
  titleRowStyle?: React.CSSProperties;
  scrollStyle?: React.CSSProperties;
};

type LocationAnalysisTimelineBodyProps = LocationAnalysisTimelineActions & {
  items: DerivedTimelineItem[];
  locations: DerivedLocation[];
  locationNameMap: Map<string, string>;
  locationPoiMap: Map<string, POIEnrichment>;
  poiCandidatesByLocationId: Map<string, POICandidate[]>;
  searchingPoiLocationId: string | null;
  linkingPoiLocationId: string | null;
  selectedTarget: DerivedTimelineTarget | null;
  editable: boolean;
  scrollStyle?: React.CSSProperties;
};

const LocationAnalysisTimelineBody: React.FC<LocationAnalysisTimelineBodyProps> = ({
  items,
  locations,
  locationNameMap,
  locationPoiMap,
  poiCandidatesByLocationId,
  searchingPoiLocationId,
  linkingPoiLocationId,
  selectedTarget,
  editable,
  scrollStyle,
  onSelect,
  onRenameLocation,
  onUpdateLocationSemanticType,
  onOverrideTriplegMode,
  onDeleteTripleg,
  onReassignStaypointLocation,
  onSearchLocationPoi,
  onLinkLocationPoi,
}) => (
  <div className="location-analysis-result-timeline-scroll" style={scrollStyle}>
    {items.length > 0 ? (
      <LocationAnalysisTimeline
        editable={editable}
        items={items}
        locations={locations}
        locationNameMap={locationNameMap}
        locationPoiMap={locationPoiMap}
        poiCandidatesByLocationId={poiCandidatesByLocationId}
        searchingPoiLocationId={searchingPoiLocationId}
        linkingPoiLocationId={linkingPoiLocationId}
        selectedTarget={selectedTarget}
        formatDateTime={formatDateTime}
        formatDistance={formatDistance}
        onSelect={onSelect}
        onRenameLocation={onRenameLocation}
        onUpdateLocationSemanticType={onUpdateLocationSemanticType}
        onOverrideTriplegMode={onOverrideTriplegMode}
        onDeleteTripleg={onDeleteTripleg}
        onReassignStaypointLocation={onReassignStaypointLocation}
        onSearchLocationPoi={onSearchLocationPoi}
        onLinkLocationPoi={onLinkLocationPoi}
      />
    ) : (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="タイムラインがありません" />
    )}
  </div>
);

export const LocationAnalysisTimelinePanel: React.FC<LocationAnalysisTimelinePanelProps> = ({
  timelineTitle = 'タイムライン',
  showTimelineEditToggle = true,
  editButtonLabel = 'タイムラインを編集',
  editingButtonLabel = '編集を終了',
  editButtonStyle,
  className,
  style,
  titleRowStyle,
  scrollStyle,
  ...timelineProps
}) => {
  const [timelineEditing, setTimelineEditing] = useState(false);
  const timelineEditable = showTimelineEditToggle ? timelineEditing : false;
  const timelineExtra = showTimelineEditToggle ? (
    <Button
      size="small"
      type={timelineEditing ? 'primary' : 'default'}
      icon={<EditOutlined />}
      onClick={() => setTimelineEditing((current) => !current)}
      style={editButtonStyle}
    >
      {timelineEditing ? editingButtonLabel : editButtonLabel}
    </Button>
  ) : null;

  return (
    <section
      className={`location-analysis-result-panel location-analysis-result-timeline-panel ${className || ''}`.trim()}
      style={style}
    >
      <div className="location-analysis-result-panel-title-row" style={titleRowStyle}>
        <div className="location-analysis-result-panel-title">{timelineTitle}</div>
        {timelineExtra}
      </div>
      <LocationAnalysisTimelineBody {...timelineProps} editable={timelineEditable} scrollStyle={scrollStyle} />
    </section>
  );
};

const LocationAnalysisResultView: React.FC<LocationAnalysisResultViewProps> = ({
  items,
  staypoints,
  triplegs,
  trips,
  locations,
  visibleLocations,
  locationExtentBufferM,
  locationNameMap,
  locationPoiMap,
  poiCandidatesByLocationId,
  searchingPoiLocationId,
  linkingPoiLocationId,
  selectedTarget,
  framed = true,
  className,
  mapTitle = '地図',
  timelineTitle = 'タイムライン',
  mapHeight = '100%',
  showTimelineEditToggle = true,
  onSelect,
  onRenameLocation,
  onUpdateLocationSemanticType,
  onOverrideTriplegMode,
  onDeleteTripleg,
  onReassignStaypointLocation,
  onSearchLocationPoi,
  onLinkLocationPoi,
}) => {
  const [timelineEditing, setTimelineEditing] = useState(false);
  const timelineEditable = showTimelineEditToggle ? timelineEditing : false;

  const mapContent = (
    <LocationAnalysisMap
      title="Derived result"
      staypoints={staypoints}
      triplegs={triplegs}
      trips={trips}
      locations={visibleLocations ?? locations}
      locationExtentBufferM={locationExtentBufferM}
      selectedDerivedTarget={selectedTarget}
      height={mapHeight}
      className="location-analysis-result-map"
    />
  );

  const timelineContent = (
    <LocationAnalysisTimelineBody
      editable={timelineEditable}
      items={items}
      locations={locations}
      locationNameMap={locationNameMap}
      locationPoiMap={locationPoiMap}
      poiCandidatesByLocationId={poiCandidatesByLocationId}
      searchingPoiLocationId={searchingPoiLocationId}
      linkingPoiLocationId={linkingPoiLocationId}
      selectedTarget={selectedTarget}
      onSelect={onSelect}
      onRenameLocation={onRenameLocation}
      onUpdateLocationSemanticType={onUpdateLocationSemanticType}
      onOverrideTriplegMode={onOverrideTriplegMode}
      onDeleteTripleg={onDeleteTripleg}
      onReassignStaypointLocation={onReassignStaypointLocation}
      onSearchLocationPoi={onSearchLocationPoi}
      onLinkLocationPoi={onLinkLocationPoi}
    />
  );

  const timelineExtra = showTimelineEditToggle ? (
    <Button
      size="small"
      type={timelineEditing ? 'primary' : 'default'}
      icon={<EditOutlined />}
      onClick={() => setTimelineEditing((current) => !current)}
    >
      {timelineEditing ? '編集を終了' : 'タイムラインを編集'}
    </Button>
  ) : null;

  if (framed) {
    return (
      <div className={`location-analysis-result-view ${className || ''}`.trim()}>
        <Card title={mapTitle} className="location-analysis-result-map-card" bodyStyle={{ height: '100%' }}>
          {mapContent}
        </Card>
        <Card
          title={timelineTitle}
          className="location-analysis-result-timeline-card"
          bodyStyle={{ padding: 0 }}
          extra={timelineExtra}
        >
          {timelineContent}
        </Card>
      </div>
    );
  }

  return (
    <div className={`location-analysis-result-view location-analysis-result-view-embedded ${className || ''}`.trim()}>
      <section className="location-analysis-result-panel">
        <div className="location-analysis-result-panel-title">{mapTitle}</div>
        {mapContent}
      </section>
      <LocationAnalysisTimelinePanel
        items={items}
        locations={locations}
        locationNameMap={locationNameMap}
        locationPoiMap={locationPoiMap}
        poiCandidatesByLocationId={poiCandidatesByLocationId}
        searchingPoiLocationId={searchingPoiLocationId}
        linkingPoiLocationId={linkingPoiLocationId}
        selectedTarget={selectedTarget}
        timelineTitle={timelineTitle}
        showTimelineEditToggle={showTimelineEditToggle}
        onSelect={onSelect}
        onRenameLocation={onRenameLocation}
        onUpdateLocationSemanticType={onUpdateLocationSemanticType}
        onOverrideTriplegMode={onOverrideTriplegMode}
        onDeleteTripleg={onDeleteTripleg}
        onReassignStaypointLocation={onReassignStaypointLocation}
        onSearchLocationPoi={onSearchLocationPoi}
        onLinkLocationPoi={onLinkLocationPoi}
      />
    </div>
  );
};

export default LocationAnalysisResultView;
